import { h } from 'preact'
import { useState } from 'preact/hooks'
import { initI18n, i18n } from 'i18n/i18n'

import { CurrencyValueForm } from 'models/apps'
import { SpinnerError } from 'models/calculator'
import { Calculation, ExchangeDirection } from 'models/exchange-transfer-calculate'

import {
  DEFAULT_EXCHANGE_DATA,
  DEFAULT_CTA_HREF,
  DEFAULT_RESPONSE,
  DEFAULT_DARK_MODE_OPTION,
  DISCOUNT_RATE,
  DEFAULT_MINIMAL_AMOUNT,
  SETTINGS_KEYS,
  DEFAULT_LANGUAGE,
  ALLOWED_WALUTOMAT_HOSTS,
  DEFAULT_REFRESH_TIME,
  FIXED_OPTION_LIST,
  DEFAULT_DEBOUNCE_TIME,
} from '../config'

import { formatCurrencyAmount } from 'app-utils/validate-currency-input'
import { validateDiscountRate } from 'app-utils/validate-discount'

import { useInitialExchangeParams } from 'hooks/useInitialExchangeParams'
import { useExchangeData } from 'hooks/useExchangeData'
import { useSettings } from 'hooks/useSettings'
import { useCurrencySelectOptions } from 'hooks/useCurrencySelectOptions'
import { useDarkMode } from 'hooks/useDarkMode'
import { useMinimalAmount } from 'hooks/useMinimalAmount'

import ErrorSpinner from 'common-modules/error-spinner'
import CalculatorActualRate from 'common-modules/exchange-calculator/actual-rate'
import CalculatorDirectionSelect from 'common-modules/exchange-calculator/direction-select'
import CalculatorInput from 'common-modules/exchange-calculator/currency-amount-input'
import CalculatorCurrencySelect from 'common-modules/exchange-calculator/currency-select'
import CalculatorFooter from 'common-modules/exchange-calculator/footer'

import CalculatorHeader from './header'

initI18n()

const MainCalculatorView = () => {
  const [direction, setDirection] = useState<ExchangeDirection>(DEFAULT_EXCHANGE_DATA.direction)
  const [baseCurrency, setBaseCurrency] = useState(DEFAULT_EXCHANGE_DATA.baseCurrency)
  const [counterCurrency, setCounterCurrency] = useState(DEFAULT_EXCHANGE_DATA.counterCurrency)
  const [value, setValue] = useState<CurrencyValueForm>(formatCurrencyAmount(DEFAULT_EXCHANGE_DATA.baseAmount))
  const [exchangeData, setExchangeData] = useState<Calculation>(DEFAULT_RESPONSE)
  const [ctaHref, setCtaHref] = useState(DEFAULT_CTA_HREF)
  const [isDarkMode, setIsDarkMode] = useState(DEFAULT_DARK_MODE_OPTION)
  const [spinnerError, setSpinnerError] = useState<SpinnerError>({ isErrorVisible: false })
  const [discountRate, setDiscountRate] = useState<number>(validateDiscountRate(DISCOUNT_RATE))

  const {
    isSettingsLoading,
    isCurrentCurrencySupported,
    setIsCurrentCurrencySupported,
    isCurrencyPairSupported,
    isCurrencySupported,
    supportedCurrenciesPairs,
    allCurrencies,
    minimalAmounts,
  } = useSettings({
    setSpinnerError,
    config: {
      SETTINGS_KEYS,
      DEFAULT_MINIMAL_AMOUNT,
    },
  })

  useInitialExchangeParams({
    isSettingsLoading,
    isCurrencyPairSupported,
    isCurrencySupported,
    setDirection,
    setBaseCurrency,
    setCounterCurrency,
    setValue,
    setCtaHref,
    setIsDarkMode,
    setDiscountRate,
    config: {
      DEFAULT_EXCHANGE_DATA,
      DEFAULT_CTA_HREF,
      DEFAULT_LANGUAGE,
      ALLOWED_WALUTOMAT_HOSTS,
      DEFAULT_DARK_MODE_OPTION,
      DISCOUNT_RATE,
    },
  })

  const { isBelowMinimalAmount } = useMinimalAmount({
    value,
    minimalAmounts,
    baseCurrency,
    counterCurrency,
  })

  useDarkMode({ isDarkMode })

  const { baseCurrencyOptions, counterCurrencyOptions } = useCurrencySelectOptions({
    baseCurrency,
    counterCurrency,
    supportedCurrenciesPairs,
  })

  useExchangeData({
    isSettingsLoading,
    direction,
    baseCurrency,
    counterCurrency,
    value,
    discountRate,
    supportedCurrenciesPairs,
    setExchangeData,
    setSpinnerError,
    isCurrencyPairSupported,
    setIsCurrentCurrencySupported,
    isBelowMinimalAmount,
    config: {
      DEFAULT_RESPONSE,
      DEFAULT_REFRESH_TIME,
      DEFAULT_EXCHANGE_DATA,
    },
  })

  return (
    <div className="calc-widget">
      <CalculatorHeader darkMode={isDarkMode} />
      <div className="calc-widget__main">
        <CalculatorActualRate
          baseCurrency={baseCurrency}
          counterCurrency={counterCurrency}
          exchangeData={exchangeData}
          allCurrencies={allCurrencies}
        />
        <div className="calc-widget__main-content">
          <div className="calc-widget__form">
            <CalculatorDirectionSelect
              direction={direction}
              setDirection={setDirection}
              isDarkMode={isDarkMode}
              options={FIXED_OPTION_LIST}
            />
            <CalculatorInput
              value={value}
              setValue={setValue}
              isBelowMinimalAmount={isBelowMinimalAmount}
              debounceTime={DEFAULT_DEBOUNCE_TIME}
            />
            <div className="calc-widget__currency-selects">
              <CalculatorCurrencySelect
                isBaseCurrencyComponent={true}
                baseCurrency={baseCurrency}
                counterCurrency={counterCurrency}
                handleSetbaseCurrency={setBaseCurrency}
                handleSetcounterCurrency={setCounterCurrency}
                fixedCurrencyList={baseCurrencyOptions}
                isDarkMode={isDarkMode}
              />
              <p className="calc-widget__for">{i18n('calculator.forBuy')}</p>
              <CalculatorCurrencySelect
                isBaseCurrencyComponent={false}
                baseCurrency={baseCurrency}
                counterCurrency={counterCurrency}
                handleSetbaseCurrency={setBaseCurrency}
                handleSetcounterCurrency={setCounterCurrency}
                fixedCurrencyList={counterCurrencyOptions}
                isDarkMode={isDarkMode}
              />
            </div>
          </div>
          {isBelowMinimalAmount && <p className="calc-widget__error-info">{i18n('calculatorForegin.minAmount')}</p>}
          {!isCurrentCurrencySupported && (
            <p className="calc-widget__error-info">{i18n('calculator.unsupportedPair')}</p>
          )}
        </div>
        <CalculatorFooter
          exchangeData={exchangeData}
          direction={direction}
          counterCurrency={counterCurrency}
          ctaHref={ctaHref}
          discountRate={discountRate}
        />
      </div>
      {spinnerError.isErrorVisible && <ErrorSpinner errorText={spinnerError.message} />}
    </div>
  )
}

export default MainCalculatorView
